import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Snackbar,
  Alert,
  LinearProgress,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  PlayCircle as PlayIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Star as StarIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';
import api from '../utils/api';

export default function HeroVideoManager({
  passageId,
  heroVideo,
  onHeroVideoChange,
  disabled = false
}) {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const fileInputRef = useRef(null);

  // Handle file selection
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    uploadVideo(file);
    event.target.value = '';
  };

  // Upload video to server
  const uploadVideo = async (file) => {
    if (!passageId) {
      setSnackbar({
        open: true,
        message: 'Please save the passage first before adding a hero video',
        severity: 'warning'
      });
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append('video', file);

    try {
      const response = await api.post(`/api/passages/${passageId}/hero-video`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });

      if (onHeroVideoChange) {
        onHeroVideoChange(response.data.heroVideo);
      }

      setSnackbar({ open: true, message: 'Hero video uploaded successfully!', severity: 'success' });
    } catch (error) {
      console.error('Error uploading hero video:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to upload hero video',
        severity: 'error'
      });
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // Delete video
  const handleDeleteVideo = async () => {
    try {
      await api.delete(`/api/passages/${passageId}/hero-video`);

      if (onHeroVideoChange) {
        onHeroVideoChange(null);
      }

      setSnackbar({ open: true, message: 'Hero video deleted successfully', severity: 'success' });
    } catch (error) {
      console.error('Error deleting hero video:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to delete hero video',
        severity: 'error'
      });
    } finally {
      setDeleteConfirm(false);
    }
  };

  return (
    <Box sx={{
      mt: 2,
      p: 2,
      background: 'linear-gradient(135deg, rgba(255, 193, 7, 0.1) 0%, rgba(255, 152, 0, 0.1) 100%)',
      borderRadius: 2,
      border: '2px solid #ff9800',
      position: 'relative'
    }}>
      {/* Hero badge */}
      <Box sx={{
        position: 'absolute',
        top: -12,
        left: 16,
        background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
        px: 2,
        py: 0.5,
        borderRadius: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        boxShadow: '0 2px 8px rgba(255, 152, 0, 0.4)'
      }}>
        <StarIcon sx={{ color: 'white', fontSize: 18 }} />
        <Typography variant="subtitle2" sx={{ color: 'white', fontWeight: 700 }}>
          HERO VIDEO
        </Typography>
      </Box>

      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          This video plays after the student clicks "Start Adventure" but before they click "Start Reading".
          Use it for avatar explanations or passage introductions.
        </Typography>

        {heroVideo ? (
          // Video exists - show preview and delete options
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            p: 2,
            background: 'rgba(255, 255, 255, 0.8)',
            borderRadius: 2,
            border: '1px solid #ffcc80'
          }}>
            <Tooltip title="Play hero video preview">
              <IconButton
                onClick={() => setPreviewOpen(true)}
                sx={{
                  bgcolor: '#ff9800',
                  color: 'white',
                  '&:hover': { bgcolor: '#f57c00', transform: 'scale(1.1)' },
                  transition: 'all 0.2s'
                }}
              >
                <PlayIcon />
              </IconButton>
            </Tooltip>

            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  icon={<CheckIcon sx={{ fontSize: 16 }} />}
                  label="Hero Video Set"
                  color="warning"
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {heroVideo.fileName || 'Video uploaded'}
              </Typography>
            </Box>

            <Tooltip title="Remove hero video">
              <IconButton
                onClick={() => setDeleteConfirm(true)}
                disabled={disabled}
                sx={{
                  color: '#f44336',
                  '&:hover': { bgcolor: 'rgba(244, 67, 54, 0.1)' }
                }}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Box>
        ) : uploading ? (
          // Uploading state
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            p: 2,
            background: 'rgba(255, 255, 255, 0.8)',
            borderRadius: 2
          }}>
            <CircularProgress size={24} sx={{ color: '#ff9800' }} />
            <Box sx={{ flex: 1 }}>
              <LinearProgress
                variant="determinate"
                value={uploadProgress}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  bgcolor: 'rgba(255, 152, 0, 0.2)',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: '#ff9800'
                  }
                }}
              />
            </Box>
            <Typography variant="body2" color="text.secondary" fontWeight={600}>
              {uploadProgress}%
            </Typography>
          </Box>
        ) : (
          // No video - show upload button
          <Box sx={{ textAlign: 'center' }}>
            <Tooltip title={passageId ? 'Upload hero video' : 'Save passage first to add hero video'}>
              <span>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={disabled || !passageId}
                  sx={{
                    borderColor: '#ff9800',
                    color: '#ff9800',
                    borderWidth: 2,
                    borderStyle: 'dashed',
                    py: 1.5,
                    px: 4,
                    '&:hover': {
                      borderColor: '#f57c00',
                      bgcolor: 'rgba(255, 152, 0, 0.1)',
                      borderWidth: 2
                    },
                    '&:disabled': {
                      borderColor: '#ccc',
                      color: '#ccc'
                    }
                  }}
                >
                  Add Hero Video
                </Button>
              </span>
            </Tooltip>
            {!passageId && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Save the passage first to enable hero video upload
              </Typography>
            )}
          </Box>
        )}
      </Box>

      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        accept="video/mp4,video/webm,video/quicktime,video/x-msvideo"
        style={{ display: 'none' }}
      />

      {/* Video Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, overflow: 'hidden' }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
          color: 'white'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <StarIcon />
            <span>Hero Video Preview</span>
          </Box>
          <IconButton onClick={() => setPreviewOpen(false)} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, bgcolor: '#000' }}>
          {heroVideo && (
            <video
              src={heroVideo.videoUrl}
              controls
              autoPlay
              style={{
                width: '100%',
                maxHeight: '70vh',
                display: 'block'
              }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ bgcolor: '#f5f5f5' }}>
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1, pl: 2 }}>
            {heroVideo?.fileName}
          </Typography>
          <Button onClick={() => setPreviewOpen(false)} sx={{ color: '#ff9800' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirm}
        onClose={() => setDeleteConfirm(false)}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle>Delete Hero Video?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the hero video? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(false)} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleDeleteVideo} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
